<template>
  <div class="hexa-stats">
    <h1>Hexa屬性模擬器</h1>
    <div class="top-section">
      <div class="total-level-card">
        <div class="hexa-core-image">
          <div class="total-level">{{ totalLevel }}</div>
        </div>
        <div class="level-display">
          {{ totalLevel }}/20
        </div>
      </div>

      <div class="preset-card">
        <div class="preset-container">
          <div 
            class="preset-item"
            :class="{ 'selected': selectedPreset === 'I' }"
            @click="selectPreset('I')"
          >
            <div class="preset-image">
              <div class="preset-number">I</div>
            </div>
          </div>
          <div 
            class="preset-item"
            :class="{ 'selected': selectedPreset === 'II' }"
            @click="selectPreset('II')"
          >
            <div class="preset-image">
              <div class="preset-number">II</div>
            </div>
          </div>
        </div>
      </div>

      <div class="button-card">
        <div class="button-group">
          <button 
            class="control-button enhance"
            @click="enhanceStats"
            :disabled="totalLevel >= 20"
          >
            強化
          </button>
          <div class="button-row">
            <button 
              class="control-button save"
              @click="savePreset"
            >
              儲存
            </button>
            <button 
              class="control-button reset"
              @click="resetStats"
              :disabled="totalLevel < 10"
            >
              初始化
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="stats-container">
      <div class="material-card">
        <div class="material-info" style="display: flex; align-items: center; justify-content: space-between;">
          <span>累積使用靈魂艾爾達碎片：<span class="material-value">{{ totalSoulAlderFragment }}</span></span>
          <button class="reset-fragment-btn" @click="resetSoulAlderFragment">重設碎片</button>
        </div>
      </div>

      <div class="stats-card">
        <div class="stat-item">
          <div class="stat-image">
            <div class="stat-level">{{ mainStatLevel }}</div>
          </div>
          <div class="stat-info">
            <div class="stat-name">主要屬性</div>
            <div class="progress-bar">
              <div 
                v-for="i in 10" 
                :key="i"
                class="progress-segment"
                :class="{ 'filled': i <= mainStatLevel }"
              ></div>
            </div>
          </div>
        </div>

        <div class="stat-item">
          <div class="stat-image">
            <div class="stat-level">{{ additionalStat1Level }}</div>
          </div>
          <div class="stat-info">
            <div class="stat-name">附加屬性 1</div>
            <div class="progress-bar">
              <div 
                v-for="i in 10" 
                :key="i"
                class="progress-segment"
                :class="{ 'filled': i <= additionalStat1Level }"
              ></div>
            </div>
          </div>
        </div>

        <div class="stat-item">
          <div class="stat-image">
            <div class="stat-level">{{ additionalStat2Level }}</div>
          </div>
          <div class="stat-info">
            <div class="stat-name">附加屬性 2</div>
            <div class="progress-bar">
              <div 
                v-for="i in 10" 
                :key="i"
                class="progress-segment"
                :class="{ 'filled': i <= additionalStat2Level }"
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { calculateTotalLevel, calculateTotalSoulAlderFragment, enhanceStats } from '@/utils/HexaStatSimulator'

const STORAGE_KEY = 'hexa-stat-simulator'

export default {
  name: 'HexaStatSimulator',
  data() {
    return {
      totalLevel: 0,
      tempMainStatLevel: 0,
      tempAdditionalStat1Level: 0,
      tempAdditionalStat2Level: 0,
      totalSoulAlderFragment: 0,
      selectedPreset: 'I',
      presets: {
        I: null,
        II: null
      }
    }
  },
  created() {
    // 從 localStorage 讀取保存的數據
    const savedData = localStorage.getItem(STORAGE_KEY)
    if (savedData) {
      const { presets } = JSON.parse(savedData)
      this.presets = presets
      // 優先選取並顯示 hexastat I 的設定
      this.selectPreset('I')
    }
  },
  computed: {
    mainStatLevel: {
      get() { return this.tempMainStatLevel },
      set(val) { this.tempMainStatLevel = val }
    },
    additionalStat1Level: {
      get() { return this.tempAdditionalStat1Level },
      set(val) { this.tempAdditionalStat1Level = val }
    },
    additionalStat2Level: {
      get() { return this.tempAdditionalStat2Level },
      set(val) { this.tempAdditionalStat2Level = val }
    }
  },
  watch: {
    mainStatLevel() {
      this.calculateTotalLevel()
    },
    additionalStat1Level() {
      this.calculateTotalLevel()
    },
    additionalStat2Level() {
      this.calculateTotalLevel()
    }
  },
  methods: {
    calculateTotalLevel() {
      this.totalLevel = calculateTotalLevel(
        this.mainStatLevel,
        this.additionalStat1Level,
        this.additionalStat2Level
      )
    },
    calculateTotalSoulAlderFragment() {
      this.totalSoulAlderFragment = calculateTotalSoulAlderFragment(
        this.mainStatLevel,
        this.additionalStat1Level,
        this.additionalStat2Level
      )
    },
    enhanceStats() {
      const oldMainLevel = this.mainStatLevel
      const oldAdditional1Level = this.additionalStat1Level
      const oldAdditional2Level = this.additionalStat2Level

      const result = enhanceStats(
        this.mainStatLevel,
        this.additionalStat1Level,
        this.additionalStat2Level
      )

      this.mainStatLevel = result.mainStatLevel
      this.additionalStat1Level = result.additionalStat1Level
      this.additionalStat2Level = result.additionalStat2Level

      // 計算新增的靈魂艾爾達碎片
      if (result.mainStatLevel > oldMainLevel) {
        this.totalSoulAlderFragment += calculateTotalSoulAlderFragment(
          result.mainStatLevel - oldMainLevel,
          0,
          0
        )
      }
      if (result.additionalStat1Level > oldAdditional1Level) {
        this.totalSoulAlderFragment += calculateTotalSoulAlderFragment(
          0,
          result.additionalStat1Level - oldAdditional1Level,
          0
        )
      }
      if (result.additionalStat2Level > oldAdditional2Level) {
        this.totalSoulAlderFragment += calculateTotalSoulAlderFragment(
          0,
          0,
          result.additionalStat2Level - oldAdditional2Level
        )
      }
    },
    selectPreset(preset) {
      this.selectedPreset = preset
      if (this.presets[preset]) {
        const { mainStatLevel, additionalStat1Level, additionalStat2Level } = this.presets[preset]
        this.tempMainStatLevel = mainStatLevel
        this.tempAdditionalStat1Level = additionalStat1Level
        this.tempAdditionalStat2Level = additionalStat2Level
      } else {
        this.tempMainStatLevel = 0
        this.tempAdditionalStat1Level = 0
        this.tempAdditionalStat2Level = 0
      }
    },
    savePreset() {
      this.presets[this.selectedPreset] = {
        mainStatLevel: this.mainStatLevel,
        additionalStat1Level: this.additionalStat1Level,
        additionalStat2Level: this.additionalStat2Level
      }
      // 保存到 localStorage
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        presets: this.presets
      }))
    },
    resetStats() {
      this.mainStatLevel = 0
      this.additionalStat1Level = 0
      this.additionalStat2Level = 0
    },
    resetSoulAlderFragment() {
      this.totalSoulAlderFragment = 0
    }
  }
}
</script>

<style scoped>
.hexa-stats {
  max-width: 800px;
  margin: 0 auto;
}

h1 {
  text-align: left;
  margin: 2rem 0;
}

.top-section {
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  width: 100%;
}

.total-level-card {
  flex: 0 0 40%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  background-color: #ffffff;
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e0e0e0;
}

.preset-card {
  flex: 0 0 26%;
  background-color: #ffffff;
  border-radius: 8px;
  padding: 1rem 0.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.button-card {
  flex: 0 0 30%;
  background-color: #ffffff;
  border-radius: 8px;
  padding: 1rem 0.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.preset-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  align-items: center;
}

.preset-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}

.preset-item.selected {
  transform: scale(1.18);
}

.preset-image {
  width: 100px;
  height: 100px;
  background-image: url('@/assets/hexastat.png');
  background-size: cover;
  background-position: center;
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.preset-number {
  font-size: 32px;
  font-weight: bold;
  color: #ffffff;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
}

.preset-name {
  font-size: 14px;
  color: #2c3e50;
}

.button-group {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
}

.button-row {
  display: flex;
  gap: 0.7rem;
}

.control-button {
  padding: 0.6rem 2rem;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(.4,0,.2,1);
  font-weight: 500;
  color: #fff;
  display: inline-block;
}

.control-button.enhance {
  background-color: var(--primary-color);
  min-width: 220px;
  font-size: 1.15rem;
  display: block;
  margin-left: auto;
  margin-right: auto;
}

.control-button.save {
  background-color: var(--primary-color);
}

.control-button.reset {
  background-color: #dc3545;
}

.control-button.enhance:hover,
.control-button.save:hover {
  background-color: var(--nav-hover);
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.control-button.reset:hover {
  background-color: #c82333;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.control-button:disabled {
  background-color: #ccc !important;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.hexa-core-image {
  width: 200px;
  height: 200px;
  background-image: url('@/assets/hexastatstotal.png');
  background-size: cover;
  background-position: center;
  border-radius: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
}

.total-level {
  font-size: 48px;
  font-weight: bold;
  color: #ffffff;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
}

.level-display {
  font-size: 24px;
  font-weight: bold;
  color: #2c3e50;
}

.stats-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.material-card {
  background-color: #ffffff;
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e0e0e0;
}

.material-info {
  font-size: 18px;
  font-weight: bold;
  color: #2c3e50;
}

.material-value {
  color: #1a237e;
}

.stats-card {
  background-color: #ffffff;
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.stat-item {
  display: flex;
  align-items: center;
}

.stat-image {
  width: 80px;
  height: 80px;
  background-image: url('@/assets/statlevel.png');
  background-size: cover;
  background-position: center;
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 1rem;
}

.stat-level {
  font-size: 24px;
  font-weight: bold;
  color: #ffffff;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
}

.stat-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.stat-name {
  font-size: 18px;
  font-weight: bold;
  color: #2c3e50;
}

.progress-bar {
  display: flex;
  gap: 4px;
  height: 20px;
}

.progress-segment {
  flex: 1;
  background-color: #e0e0e0;
  border-radius: 4px;
}

.progress-segment.filled {
  background-color: #1a237e;
}

.reset-fragment-btn {
  margin-left: 1.5rem;
  padding: 0.4rem 1.2rem;
  background-color: #dc3545;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.2s;
}
.reset-fragment-btn:hover {
  background-color: #c82333;
}
</style> 